import { Injectable } from '@angular/core';
import {
  HttpInterceptor as HI,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
  HttpHeaders,
  HttpContextToken,
} from '@angular/common/http';

/** When set to `true` on a request's HttpContext, the global full-page loader is skipped. */
export const SKIP_LOADER = new HttpContextToken<boolean>(() => false);

import { Observable, from, of, timer, EMPTY, BehaviorSubject, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { LoadingService } from '../services/loading.service';
import { AuthService } from '../services/auth.service';
import { AuthService as Auth0} from '@auth0/auth0-angular';
import { CONTENT_TYPES } from 'src/app/shared/constants';
import {
  AlertService,
  ToastTypes,
} from 'src/app/shared/services/alert.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RouteService } from '../services/route.service';
import { environment } from 'src/environments/environment';
import { StorageService } from '../services/storage.service';
import { OGAuth0Service } from '../services/og-auth0.service';

/**
 * Intercept http request and response to set loading
 */
@Injectable()
export class HttpInterceptor implements HI {
  refreshTokenRequest: any = null;
  refreshTokenInProgress = false;
  tokenSubject = new BehaviorSubject<any>(null);
  lastFailedRequest: HttpRequest<any> | null = null;
  /**
   * Routes which don't require tokens
   */
  unAuthorizedUrls: Array<string> = [
    '/token',
    '/user/validemail',
    '/user/validatetoken',
    '/application/applogininfo',
    '/customcss/getappdefaultcss',
    '/user/register',
    'api/user/login',
    'payment/addPaymentMethod',
    'user/checktwofactor',
    'user/forgotpassword',
    'user/recoverpassword',
    'user/resendmfa',
    'notification/executeverifiednotification',
  ];

  tableLoadingUrls = [
    '/getdatatabledata',
    '/getreportcolumns',
    '/getformforreport',
    '/getreportbyid'
  ]

  noLoaderURL = [
    '/getnavigation',
    '/applicationdata',
    '/user/logout'
  ]

  dropDownApiArr = [
    '/company',
    '/application?id=',
    '/getdetailsbyid',
    'getprocessview?recordId',
    '/gettablefields?id=',
    '/getbytableid?Id=',
    '/getprocessesbytableid?Id=',
    '/getseriesfieldsfortable?tableId=',
    '/getbyapplicationid?id=',
    '/getfieldtypes',
    '/getapplicationroles?applicationId=',
    '/applicationdata?applicationId=',
    '/getnotapplicationid?applicationId=',
    '/getreportbytableid?id=',
    '/getformfields?id=',
    '/getpagebyapplicationid?id=',
    '/dashboards?id=',
    '/getbytableid?id=',
    '/formfieldusers?tableId='
  ];

  statusUrlMapForErrorForceThrow: { [key: number]: {[key: string]: string[]} } = {
    404: { GET: ['/workflow/getbywebhookurl?tableId='] },
    400: { PATCH: ['/rest/workflows'] }
  }

  constructor(
    private loadingService: LoadingService,
    private authService: AuthService,
    private alert: AlertService,
    private ngbModal: NgbModal,
    private route: RouteService,
    private storage: StorageService,
    private readonly auth0Service: Auth0,
    private readonly ogAuth0Service: OGAuth0Service
  ) {}

  private handleAuthError(
    err: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    this.loadingService.loadingState = false;
    this.loadingService.tableLoadingState = false;

    if(this.statusUrlMapForErrorForceThrow[err.status]?.[request.method]?.some(url => err.url?.includes(url))) {
      throw err;
    }

    // Handle 401 and 400 errors
    if (err.status === 401 || err.status === 400) {
      return this.handle401And400Errors(err, request, next);
    }

    // Handle password recovery errors
    if (err.url?.includes('/recoverpassword') || err.url?.includes('/passwordrecovery')) {
      this.googleRecaptchaError(err);
      return of(false);
    }

    // Handle CORS errors (status 0)
    if (err.status === 0) {
      return this.handleCorsErrors(err, request, next);
    }

    // Handle executeverifiednotification 403 errors first
    if (err.status === 403 && err.url?.includes('/executeverifiednotification')) {
      throw err;
    }

    // Handle other 403 errors
    if (err.status === 403) {
      return this.handle403Errors(err);
    }

    // Handle 404 application errors
    if (this.isApplicationNotFoundError(err)) {
      return this.handleApplicationNotFoundError();
    }

    // Handle userExists errors
    if (this.isUserExistsError(err)) {
      this.authService.logout(true, true, this.storage.getPortalId());
      return EMPTY;
    }

    // Handle other errors that should be thrown
    if (this.shouldThrowError(err)) {
      throw err;
    }

    this.loadingService.loadingState = false;
    this.loadingService.tableLoadingState = false;
    return of(false);
  }

  private handle401And400Errors(
    err: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    // Do not force logout on external login user existence probe
    if (err.url?.includes('/user/userExists')) {
      throw err;
    }

    if (err.url?.includes('login')) {
      return this.handleLoginErrors(err);
    }

    if (err.url?.includes('/token')) {
      return this.handleTokenErrors(err);
    }

    if (err.error === 'User is not authorized.') {
      this.authService.logout(false, false, this.storage.getPortalId());
      return of(false);
    }

    if (err.error?.Message === 'Authorization has been denied for this request.') {
      return this.checkRefreshTokenHandling(err, request, next);
    }

    if (err.error?.error === 'invalid_grant') {
      return this.logoutWithToaster();
    }

    if (err.status === 401) {
      return this.handle401Errors(err);
    }

    if (err.status === 400) {
      return this.handle400Errors(err);
    }

    return of(false);
  }

  private handleLoginErrors(err: HttpErrorResponse): Observable<any> {
    if (err.error?.Message?.includes("inactive")) {
      this.logoutWithToaster(err.error);
      return EMPTY;
    } else if (err.error === 'Passcode failed.') {
      this.passcodeError(err);
      throw err;
    } else {
      this.loadingService.loadingState = false;
      this.loadingService.tableLoadingState = false;
      this.googleRecaptchaError(err);
      throw err;
    }
  }

  private handleTokenErrors(err: HttpErrorResponse): Observable<any> {
    if (err.error?.error_description?.includes('password is incorrect')) {
      this.loadingService.loadingState = false;
      return of(false);
    } else if (err.error?.includes("inactive")) {
      this.logoutWithToaster(err.error);
      return EMPTY;
    } else {
      this.logoutWithToaster();
      return EMPTY;
    }
  }

  private handle401Errors(err: HttpErrorResponse): Observable<any> {
    if (err.url?.includes('/company')) {
      throw err;
    } else {
      this.logoutWithToaster();
      return EMPTY;
    }
  }

  private handle400Errors(err: HttpErrorResponse): Observable<any> {
    if (err.url?.includes('/resendinvite') ||
        err.url?.includes('/createmultiplefields') ||
        err.url?.endsWith('/import') ||
        err.url?.includes('/checktwofactor')) {
      throw err;
    } else {
      this.alert.toaster(err.error);
      return of(false);
    }
  }

  private handleCorsErrors(
    err: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    if (err.url?.includes('/getprofile')) {
      // A CORS/network error on /getprofile is not necessarily a session expiry.
      // If the user has an active session they will be silently re-authenticated;
      // showing the "Session has expired" toast in that case is misleading and noisy.
      // Log out quietly and let the auto-login (or explicit login) flow handle messaging.
      this.authService.logout(true, false, this.storage.getPortalId());
      return EMPTY;
    }

    // for safety, occurs in case of CORS Error, app logs out
    if (err.statusText === 'Unknown Error') {
      return this.checkRefreshTokenHandling(err, request, next);
    } else if (err.url?.includes('/token')) {
      // logging out here because in case of CORS error, undesired requests are not allowed
      return this.logoutWithToaster();
    }

    return of(false);
  }

  private handle403Errors(err: HttpErrorResponse): Observable<any> {
    this.loadingService.loadingState = false;
    this.loadingService.tableLoadingState = false;

    // Handle disabled/inactive account (from GetLoggedUser or any API endpoint)
    const errorMessage = typeof err.error === 'string' ? err.error : err.error?.Message || err.error?.message || ''; // NOSONAR - intentional || for falsy fallback
    if (errorMessage.toLowerCase().includes('inactive') || errorMessage.toLowerCase().includes('disabled')) {
      this.alert.toast(
        ToastTypes.ERROR,
        'Account Disabled',
        errorMessage
      );
      this.authService.logout(false, true, this.storage.getPortalId());
      return EMPTY;
    }

    this.alert.toast(
      ToastTypes.WARNING,
      'WARNING',
      err.error || 'Access permission denied.'
    );
    if (!err.url?.includes('/executeverifiednotification')) {
      this.route.navigate(['/apps']);
      return of(false);
    }
    return of(false);
  }

  private isApplicationNotFoundError(err: HttpErrorResponse): boolean {
    return err.status === 404 && (
      (err.url?.includes('/application/applogininfo') ?? false) ||
      (err.url?.includes('/customcss/getappdefaultcss') ?? false)
    );
  }

  private handleApplicationNotFoundError(): Observable<any> {
    try { this.storage.savePortalId(null as any); } catch {}
    try { this.storage.clearRedirectURL?.(); } catch {}
    this.alert.toast(ToastTypes.ERROR, 'ERROR', 'Application not found.');
    this.route.navigate(['/auth','login']);
    return EMPTY;
  }

  private isUserExistsError(err: HttpErrorResponse): boolean {
    return (err.status === 403 || err.status === 500 || err.status === 400 || err.status === 404) &&
           (err.url?.includes('/userExists') ?? false);
  }

  private shouldThrowError(err: HttpErrorResponse): boolean {
    return err.status === 409 ||
           err.status === 500 ||
           err.status === 422 ||
           (err.status === 404 && this.dropDownApiArr.some(endpoint => err.url?.includes(endpoint)));
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const pattern = this.unAuthorizedUrls
    .map(u => `${u}(?:\\?.*)?$`) // match exact path with optional query params
    .join('|');
    if (new RegExp(pattern).test(request.url))
      return next.handle(request).pipe(
        this.handleLoadingUnset(),
        catchError((response) => this.handleAuthError(response, request, next))
      );
    // Block navigation call until authenticated
    if (request.url.includes('/api/user/getnavigation')) {
      const isAuthed = !!this.storage.getToken();
      if (!isAuthed) {
        return EMPTY;
      }
    }
    const skipLoader = request.context.get(SKIP_LOADER);
    if(!skipLoader && !(new RegExp(this.tableLoadingUrls.join('|')).test(request.url)) && !(new RegExp(this.noLoaderURL.join('|')).test(request.url))) {
      if(!this.lastFailedRequest){
        this.loadingService.loadingState = true;
      }
    }
    if(new RegExp(this.tableLoadingUrls.join('|')).test(request.url)){
      this.loadingService.tableLoadingState = true;
    }
    return from(this.authService.refreshToken()).pipe(
      switchMap((access_token) => {
        request = request.clone({ url: request.url });
        if (!access_token) {
          return next.handle(request);
        }
        let headers = new HttpHeaders();
        if (!request.url.includes('uploadlogo') && !request.url.includes('docsave') && !request.url.includes('templatesave'))
          headers = headers.set('Content-Type', CONTENT_TYPES.APPLICATION_JSON);
        const isWorkflowRequest = request.url.startsWith(environment.workflowUrl);
        if (isWorkflowRequest) {
          headers = headers.set('Authorization', access_token);
          headers = headers.set('x-opengov-client-mode', 'embedded');
        } else {
          headers = headers.set('Authorization', `Bearer ${access_token}`);
        }
        if(this.storage.getImpersonateId() != null){
            let impersonate_key: string = this.storage.getImpersonateId() ?? '';
            headers = headers.set('Impersonation', impersonate_key);
        }
        if(this.storage.getIsExternalLogin()){
          headers = headers.set('IsExternalLogin', 'true');
        }

        const modifiedReq = request.clone({
          headers,
        });
        return next.handle(modifiedReq).pipe(
          this.handleLoadingUnset(),
          catchError((response) => {
            return this.handleAuthError(response, request, next);
          })
        );
      })
    );
  }

  replaceToken = (req: HttpRequest<any>, token: string) => {
    let headers = new HttpHeaders();
    if (!req.url.includes('uploadlogo') && !req.url.includes('docsave') && !req.url.includes('templatesave')) {
      headers = headers.set('Content-Type', CONTENT_TYPES.APPLICATION_JSON);
    }
    headers = headers.set('Authorization', `Bearer ${token}`);
    if(this.storage.getIsExternalLogin()){
      // headers = headers.set('withCredentials', 'true');
      headers = headers.set('IsExternalLogin', 'true');
    }
    const modifiedReq = req.clone({
      headers
    });
    return modifiedReq;
  };

  handleLoadingUnset = () =>
    tap(
      (ev: HttpEvent<any>) => {
        if (ev instanceof HttpResponse) {
          this.loadingService.loadingState = false;
          this.loadingService.tableLoadingState = false;
        }
      },
      (err: any) => {
        this.loadingService.errorType = err?.error?.error
          ? err?.error?.error
          : '';
        this.loadingService.loadingState = false;
        this.loadingService.tableLoadingState = false;
      }
    );

  private getAuth0TokenSource(options?: any): Observable<any> {
    const authProvider = this.storage.getAuthProvider();
    if (authProvider === 'og-auth0') {
      return this.ogAuth0Service.getAccessTokenSilently(options);
    }
    return this.auth0Service.getAccessTokenSilently(options).pipe(take(1));
  }

  private selectTokenSource(): Observable<any> {
    if (this.storage.getAuthProvider()) {
      return this.getAuth0TokenSource({detailedResponse:true}).pipe(
        tap((token:any) => this.authService.setAllTokens(token)),
        map(token => token.access_token),
        catchError(() => {
          this.refreshTokenInProgress = false;
          return this.logoutWithToaster();
        })
      );
    }
    if (this.storage.getIsExternalLogin()) {
      return this.auth0Service.getAccessTokenSilently({detailedResponse:true}).pipe(
        tap((token:any) => this.authService.setAllTokens(token)),
        map(token => token.access_token)
      );
    }
    return from(this.authService.getRefreshTokenByApi());
  }

  handleMainTokenRefresh(
    request: HttpRequest<any>,
    next: HttpHandler
  ) {
    if (request.url.includes('user/logout')) {
      return this.logoutWithToaster();
    }
    if (!this.refreshTokenInProgress) {
      this.refreshTokenInProgress = true;
      return timer(2000).pipe(
        switchMap(() => this.selectTokenSource()),
        finalize(() => (this.refreshTokenInProgress = false)),
        switchMap((access_token: string) => {
          this.refreshTokenInProgress = false;
          this.lastFailedRequest = null;
          if (access_token) {
            this.tokenSubject.next(access_token);
            return next.handle(this.replaceToken(request, access_token));
          }
          return throwError(() => {});
        })
      );
    }
    return this.tokenSubject.pipe(
      take(1),
      switchMap((token: string) => {
        if (token === null) {
          return EMPTY;
        }
        return next.handle(this.replaceToken(request, token));
      }),
    );
  }

  checkRefreshTokenHandling(
    err: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler
  ) {
    return this.handleMainTokenRefresh(request, next);
  }

  protected getCurrentPathname(): string {
    return window.location.pathname;
  }

  logoutWithToaster(errorMessage: string = '') {
    if (this.getCurrentPathname() === '/callback-handler') {
        return EMPTY;
    }
    this.authService.logout(true, false, this.storage.getPortalId());
    errorMessage != '' ? this.alert.toaster(errorMessage) : this.alert.toaster('Session has expired, please login again.');
    return EMPTY;
  }

  googleRecaptchaError(err: HttpErrorResponse) {

    if (err?.error?.Message?.includes('GoogleRecaptcha verification failed')) {
      this.alert.toast(ToastTypes.ERROR, 'Error', err?.error?.Message);
      this.authService.logout(true, false, this.storage.getPortalId());
      this.alert.toast(
        ToastTypes.ERROR,
        'GoogleRecaptcha verification failed.',
        'Please try to login again.'
      );
    } else {
      // Handle error object properly to avoid [object Object]
      let errorMessage = 'An error occurred.';
      if (typeof err?.error === 'string') {
        errorMessage = err.error;
      } else if (err?.error?.Message) {
        errorMessage = err.error.Message;
      } else if (err?.error?.error) {
        errorMessage = err.error.error;
      } else if (err?.error && typeof err.error === 'object') {
        // If it's an object, try to extract meaningful message
        const errorObj = err.error;
        if (errorObj.message) {
          errorMessage = errorObj.message;
        } else if (errorObj.error_description) {
          errorMessage = errorObj.error_description;
        } else {
          // Fallback to a generic message instead of [object Object]
          errorMessage = 'Please check your Email and Password';
        }
      }
      this.alert.toast(ToastTypes.ERROR, 'Error', errorMessage);
    }
  }

  passcodeError(err: HttpErrorResponse) {
    this.authService.logout(true, false, this.storage.getPortalId());
      this.alert.toast(
        ToastTypes.ERROR,
        err.error,
        'MFA code entered is not valid.'
      );
  }
}
